// src/components/store/ProductRecommendations.tsx
"use client";

import { useEffect, useState, useCallback } from "react";
import AiRecommenderSectionRenderer from "./sections/ai-recommender";

interface ProductRecommendationsProps {
  subdomain: string;
  productId?: number | string;
  customerId?: number | string;
  customerEmail?: string;
  title?: string;
  subtitle?: string;
  limit?: number;
  primaryColor?: string;
  variant?: string;
}

const SESSION_ID_KEY = "xylo_ai_session_id";
const SESSION_CLICKS_KEY = "xylo_ai_session_clicks";

/**
 * Browser-e unique session_id toiri o retrieve kore (Session GRU-er jonno)
 */
export function getOrCreateAiSessionId(): string {
  if (typeof window === "undefined") return "server_session";
  let sid = localStorage.getItem(SESSION_ID_KEY);
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    localStorage.setItem(SESSION_ID_KEY, sid);
  }
  return sid;
}

/**
 * Customer-er live click sequence (max 15 items) localStorage theke ney
 */
export function getAiSessionClicks(): number[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(SESSION_CLICKS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((id) => Number(id)).filter((id) => !isNaN(id) && id > 0);
    }
  } catch {}
  return [];
}

/**
 * Logged-in customer ID ebong Email detect kore (views.py _extract_recommendation_params onujayi)
 */
export function detectCustomerIdentity(
  propCustomerId?: number | string,
  propCustomerEmail?: string
): { customerId: string; customerEmail: string } {
  let customerId = propCustomerId ? String(propCustomerId) : "";
  let customerEmail = propCustomerEmail ? String(propCustomerEmail) : "";

  if (typeof window === "undefined") {
    return { customerId, customerEmail };
  }

  try {
    if (!customerId) {
      const directId =
        localStorage.getItem("customer_id") || localStorage.getItem("user_id");
      if (directId && directId !== "null" && directId !== "undefined") {
        customerId = String(directId);
      }
    }

    if (!customerEmail) {
      const directEmail =
        localStorage.getItem("customer_email") || localStorage.getItem("user_email");
      if (directEmail && directEmail.includes("@")) {
        customerEmail = String(directEmail);
      }
    }

    const rawObj =
      localStorage.getItem("customer_info") ||
      localStorage.getItem("customer") ||
      localStorage.getItem("user");

    if (rawObj) {
      const parsed = JSON.parse(rawObj);
      if (!customerId) {
        const foundId = parsed?.id || parsed?.customer_id || parsed?.user?.id;
        if (foundId) customerId = String(foundId);
      }
      if (!customerEmail) {
        const foundEmail = parsed?.email || parsed?.customer_email || parsed?.user?.email;
        if (foundEmail && String(foundEmail).includes("@")) {
          customerEmail = String(foundEmail);
        }
      }
    }
  } catch {}

  return { customerId, customerEmail };
}

/**
 * Customer kono product view ba click korle localStorage ebong Backend-er
 * TrackSessionClickAPIView (/api/v1/ai/recommend/track-click/)-te sync kore
 */
export async function recordAiProductClick(
  productId: number | string,
  subdomain: string = "nextlook"
) {
  if (typeof window === "undefined" || !productId) return;
  const pid = Number(productId);
  if (isNaN(pid) || pid <= 0) return;

  let updatedClicks: number[] = [];
  try {
    const current = getAiSessionClicks().filter((id) => id !== pid);
    current.push(pid);
    updatedClicks = current.slice(-15);
    localStorage.setItem(SESSION_CLICKS_KEY, JSON.stringify(updatedClicks));
  } catch {}

  // Backend-er TrackSessionClickAPIView-e live click pathano
  try {
    const isLocal =
      window.location.hostname.includes("localhost") ||
      window.location.hostname === "127.0.0.1";
    const baseUrl = isLocal ? `http://${subdomain}.localhost:8000` : "";
    const sessionId = getOrCreateAiSessionId();
    const { customerId, customerEmail } = detectCustomerIdentity();

    const payload = {
      subdomain,
      product_id: pid,
      session_id: sessionId,
      session_clicks: updatedClicks,
      ...(customerId ? { customer_id: customerId } : {}),
      ...(customerEmail ? { customer_email: customerEmail } : {}),
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      "x-subdomain": subdomain,
      "X-Tenant-Subdomain": subdomain,
      "X-Session-ID": sessionId,
    };

    await fetch(`${baseUrl}/api/v1/ai/recommend/track-click/`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {}
}

/**
 * Backend-er utils.py (serialize_recommendation_item) theke asha product data normalize kore
 */
function normalizeRecommendedProduct(item: any) {
  const prod = item?.product && typeof item.product === "object" ? item.product : item;
  if (!prod) return null;

  const imageUrl =
    prod.image ||
    prod.primary_image_url ||
    (Array.isArray(prod.product_images) && prod.product_images[0]?.image) ||
    (Array.isArray(prod.images) && prod.images[0]) ||
    null;

  const price = Number(prod.price ?? 0);
  const comparePrice = prod.compare_price ? Number(prod.compare_price) : null;
  const discountPct =
    prod.discount_percentage ||
    (comparePrice && comparePrice > price
      ? Math.round(((comparePrice - price) / comparePrice) * 100)
      : 0);

  return {
    ...prod,
    id: prod.id || item.product_id,
    name: prod.name || "Product",
    slug: prod.slug || String(prod.id || item.product_id),
    unit: prod.unit || "1 pc",
    price,
    compare_price: comparePrice,
    discount_percentage: discountPct,
    custom_badge:
      prod.custom_badge || (discountPct > 0 ? `${discountPct}% OFF` : ""),
    category_name:
      prod.category_name ||
      prod.category?.name ||
      (Array.isArray(prod.categories) && prod.categories[0]?.name) ||
      "General",
    rating: Number(prod.rating ?? 5.0),
    total_reviews: Number(prod.total_reviews ?? 0),
    image: imageUrl,
    primary_image_url: imageUrl,
    in_stock:
      typeof prod.in_stock === "boolean"
        ? prod.in_stock
        : Number(prod.stock_quantity ?? 1) > 0,
    recommendation_score: Number(
      prod.recommendation_score ?? item.score ?? 0
    ),
  };
}

export default function ProductRecommendations({
  subdomain,
  productId,
  customerId,
  customerEmail,
  title = "Recommended For You",
  subtitle = "AI-powered picks based on your preferences & trending items",
  limit = 8,
  primaryColor = "#059669",
  variant = "floating_circle",
}: ProductRecommendationsProps) {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadRecommendedProducts = useCallback(async () => {
    setLoading(true);
    try {
      if (productId) {
        recordAiProductClick(productId, subdomain);
      }

      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");

      const baseUrl = isLocal ? `http://${subdomain}.localhost:8000` : "";
      const sessionId = getOrCreateAiSessionId();
      const sessionClicks = getAiSessionClicks();
      const { customerId: resolvedId, customerEmail: resolvedEmail } =
        detectCustomerIdentity(customerId, customerEmail);

      const queryParams = new URLSearchParams();
      queryParams.set("subdomain", subdomain);
      queryParams.set("limit", String(limit));
      queryParams.set("session_id", sessionId);

      if (productId) {
        queryParams.set("product_id", String(productId));
      }
      if (resolvedId) {
        queryParams.set("customer_id", resolvedId);
      }
      if (resolvedEmail) {
        queryParams.set("customer_email", resolvedEmail);
      }
      if (sessionClicks.length > 0) {
        queryParams.set("session_clicks", sessionClicks.join(","));
      }

      const token =
        typeof window !== "undefined"
          ? localStorage.getItem("customer_token") ||
            localStorage.getItem("token") ||
            ""
          : "";

      const headers: Record<string, string> = {
        Accept: "application/json",
        "x-subdomain": subdomain,
        "X-Tenant-Subdomain": subdomain,
        "X-Session-ID": sessionId,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // Matches apps/ai_engine/urls.py routes (ProductRecommendationAPIView & AIRecommendationViewSet.feed)
      const candidateEndpoints = [
        `${baseUrl}/api/v1/ai/recommend/?${queryParams.toString()}`,
        `${baseUrl}/api/v1/ai/products/recommend/?${queryParams.toString()}`,
        `${baseUrl}/api/v1/ai/recommendations/feed/?${queryParams.toString()}`,
        `${baseUrl}/api/v1/ai-engine/recommend/?${queryParams.toString()}`,
        `${baseUrl}/api/v1/products/?subdomain=${encodeURIComponent(subdomain)}&limit=${limit}`,
      ];

      let loadedList: any[] = [];

      for (const endpoint of candidateEndpoints) {
        try {
          const res = await fetch(endpoint, { headers });
          if (res.ok) {
            const data = await res.json();
            const rawList = Array.isArray(data)
              ? data
              : data?.recommendations || data?.results || data?.data || [];

            if (Array.isArray(rawList) && rawList.length > 0) {
              loadedList = rawList
                .map(normalizeRecommendedProduct)
                .filter(Boolean);
              if (loadedList.length > 0) {
                break;
              }
            }
          }
        } catch {
          // Try next endpoint
        }
      }

      setProducts(loadedList.slice(0, limit));
    } catch (err) {
      console.error("Error loading AI recommended products:", err);
    } finally {
      setLoading(false);
    }
  }, [subdomain, productId, customerId, customerEmail, limit]);

  useEffect(() => {
    loadRecommendedProducts();
  }, [loadRecommendedProducts]);

  if (loading || products.length === 0) return null;

  return (
    <AiRecommenderSectionRenderer
      variant={variant}
      products={products}
      title={title}
      subtitle={subtitle}
      primaryColor={primaryColor}
      subdomain={subdomain}
    />
  );
}